import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { MilestoneMap1, Objective1, Status1 } from '../types';
import { TreeData } from './TreeViewTypes';
import { updateObjective, updateMilestoneMap } from '../store/map3PendingSlice';

// Import hooks for dependency logic
import { useAllRelativesData } from '../map_dependencies/hooks/useAllRelativesData';

// New architecture imports
import {
  buildKeyDetailsDataNode,
  buildObjectivesDataNode,
  buildDatesDataNode,
} from './data_nodes/builders';
import { DataNode } from './data_nodes/types';
import { convertToTreeData } from './conversion/converter';
import { registerAllLabelGenerators } from './label_generators/registerAllGenerators';

// Legacy imports for complex Status Report Date labels (to be removed in Phase 5)
import { getIntervalsForMilestoneMap } from '@mm-shared/intervals';
import { countObjectiveStatuses, analyzeObjectiveStatus } from '../utils/statusSummaryUtils';
import { getChildObjectivesForParentObjectiveByOrder } from '@mm-shared/hierarchy';
import { filterItemsForInterval } from '@mm-shared/intervals';
import { AltLabel, LeadingTypeIcon, TrailingVariantIcon, TreeItemData } from './TreeViewTypes';

interface EmbeddedTreeViewProps {
  milestoneMapId?: string;
  includeRelatedMaps?: boolean;
}

interface MessageData {
  type: string;
  payload?: any;
}

interface MilestoneMapDataPayload {
  trees: TreeData[];
  milestoneMapId: string;
  timestamp: string;
  defaultExcludedDataTypes?: string[];
}

// Internal DebugInfo component
interface DebugInfoProps {
  isLocalDev: boolean;
  localPort: string | null;
  iframeUrl: string;
  isIframeReady: boolean;
}

function DebugInfo({ isLocalDev, localPort, iframeUrl, isIframeReady }: DebugInfoProps) {
  if (!isLocalDev) {
    return null;
  }

  return (
    <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
      <strong>Debug Info:</strong>
      <br />
      Environment: 🔧 Local Development (port {localPort})
      <br />
      Iframe URL: {iframeUrl}
      <br />
      Iframe Ready: {isIframeReady ? '✅' : '❌'}
      <br />
      Check browser console for detailed messaging logs
      <br />
      <strong>💡 Development Mode:</strong> Add <code>?local=3000</code> to use localhost:3000
    </div>
  );
}

// Data transformation functions removed - using the new function declarations below

// Helper function to get default label value from altLabels
function getDefaultLabel(altLabels: AltLabel[]): string {
  const defaultLabel = altLabels.find((label) => label.key === 'default');
  if (!defaultLabel) {
    console.error('Error: No default label found in altLabels:', altLabels);
    return 'Error: No default label found';
  }
  return defaultLabel.value;
}

// Utility function to create AltLabels
function createAltLabels(
  type: string,
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  suggestedLabel: string,
  uuid?: string,
): AltLabel[] {
  switch (type) {
    case 'Milestone Map':
      // Filter to only child objectives (not parents) for status counting
      const isParent = (objectiveUuid: string): boolean => {
        return milestoneMap?.parentObjectiveUuids?.includes(objectiveUuid) ? true : false;
      };
      const childObjectives = objectives.filter((obj) => !isParent(obj.uuid));
      const statusCounts = countObjectiveStatuses(childObjectives);

      return [
        {
          key: 'default',
          selectOption: `Default: Title (N green, N yellow, N red)`,
          value: `${milestoneMap.title} (${statusCounts.green} green, ${statusCounts.yellow} yellow, ${statusCounts.red} red)`,
        },
        {
          key: 'short',
          selectOption: `Short: Title (N g, N y, N r)`,
          value: `${milestoneMap.title} (${statusCounts.green} g, ${statusCounts.yellow} y, ${statusCounts.red} r)`,
        },
        {
          key: 'title',
          selectOption: `Title-only`,
          value: milestoneMap.title,
        },
      ];

    case 'Major Objective':
      if (!uuid) {
        // Fallback if no UUID provided
        return [
          {
            key: 'default',
            selectOption: 'Default',
            value: suggestedLabel,
          },
        ];
      }

      // Use the shared helper function to get child objectives
      const majorObjChildObjectives = getChildObjectivesForParentObjectiveByOrder(
        milestoneMap,
        objectives,
        uuid,
      );

      // Calculate status counts for child objectives
      const majorObjStatusCounts = countObjectiveStatuses(majorObjChildObjectives);

      return [
        {
          key: 'default',
          selectOption: `Default: [title] (N green, N yellow, N red)`,
          value: `${suggestedLabel} (${majorObjStatusCounts.green} green, ${majorObjStatusCounts.yellow} yellow, ${majorObjStatusCounts.red} red)`,
        },
        {
          key: 'short',
          selectOption: `Short: [title] (N g, N y, N r)`,
          value: `${suggestedLabel} (${majorObjStatusCounts.green} g, ${majorObjStatusCounts.yellow} y, ${majorObjStatusCounts.red} r)`,
        },
        {
          key: 'title',
          selectOption: `Title-only: [title]`,
          value: suggestedLabel,
        },
      ];

    case 'Objective':
      // Find the specific objective to get its status and owner info
      const currentObjective = objectives.find((obj) => obj.uuid === uuid);

      if (!currentObjective) {
        // Fallback if objective not found
        console.log(`⚠️ Objective not found for uuid=${uuid}, returning basic labels`);
        return [
          {
            key: 'default',
            selectOption: 'Default',
            value: suggestedLabel,
          },
        ];
      }

      // Get the latest status analysis (same logic as generateObjectiveSummary in Map3.tsx)
      const statusAnalysis = analyzeObjectiveStatus(currentObjective);
      const statusDisplay = statusAnalysis.displayName || 'no status';
      const statusShort = statusAnalysis.statusCode
        ? statusAnalysis.statusCode.toLowerCase() === 'g'
          ? 'g'
          : statusAnalysis.statusCode.toLowerCase() === 'y'
            ? 'y'
            : statusAnalysis.statusCode.toLowerCase() === 'r'
              ? 'r'
              : statusAnalysis.statusCode.toLowerCase() === 'p'
                ? 'p'
                : statusAnalysis.statusCode.toLowerCase() === '?'
                  ? '?'
                  : 'n'
        : 'n';

      // Truncate title for short version (limit to 30 characters)
      const truncatedTitle =
        suggestedLabel.length > 30 ? suggestedLabel.substring(0, 27) + '...' : suggestedLabel;

      // Always provide the same 5 keys for all objectives for consistency
      const ownerDisplay = currentObjective.ownerName || 'unassigned';

      const labels = [
        {
          key: 'default',
          selectOption: `Default: Title (status)`,
          value: `${suggestedLabel} (${statusDisplay})`,
        },
        {
          key: 'title-owner-status-with-labels',
          selectOption: `Title, owner, status with labels`,
          value: `${suggestedLabel} (owner: ${ownerDisplay} status: ${statusDisplay})`,
        },
        {
          key: 'title-owner-status',
          selectOption: `Title, owner, status`,
          value: `${suggestedLabel} (${ownerDisplay}, ${statusDisplay})`,
        },
        {
          key: 'short',
          selectOption: `Short`,
          value: `${truncatedTitle} (${statusShort})`,
        },
        {
          key: 'title',
          selectOption: `Title-only`,
          value: suggestedLabel,
        },
      ];

      return labels;

    case 'Owner':
      return [
        {
          key: 'default',
          selectOption: 'Default',
          value: suggestedLabel,
        },
      ];

    case 'Plans & Tasks':
      return [
        {
          key: 'default',
          selectOption: 'Default',
          value: suggestedLabel,
        },
      ];

    case 'Key Result':
      return [
        {
          key: 'default',
          selectOption: 'Default',
          value: 'Key Results',
        },
      ];

    case 'Result':
      return [
        {
          key: 'default',
          selectOption: 'Default',
          value: suggestedLabel,
        },
      ];

    case 'Additional Information':
      return [
        {
          key: 'default',
          selectOption: 'Default',
          value: suggestedLabel,
        },
      ];

    case 'Status Report Date':
      const statusUuid = uuid;

      // Get forIntervalEnding from statusUuid in objective.statuses[].uuid
      let forIntervalEnding = '';
      let currentStatus: Status1 | undefined = undefined;

      // Search through all objectives to find the status with matching UUID
      for (const objective of objectives) {
        const status = objective.statuses.find((s) => s.uuid === statusUuid);
        if (status) {
          forIntervalEnding = status.forIntervalEnding;
          currentStatus = status;
          break; // Found the status, exit the loop
        }
      }

      // If no status was found after checking all objectives, return fallback
      if (!currentStatus || !forIntervalEnding) {
        console.error(
          `Error: No status found for statusUuid=${statusUuid} across all objectives in milestone map=${milestoneMap.uuid}`,
        );
        return [
          {
            key: 'default',
            selectOption: 'Default',
            value: suggestedLabel,
          },
        ];
      }

      const parsedDate = new Date(forIntervalEnding);

      // Get intervals for this milestone map
      const intervals = getIntervalsForMilestoneMap(milestoneMap);

      // Find the interval that ends on this date
      const currentInterval = intervals.find(
        (interval) => interval.end.toDateString() === parsedDate.toDateString(),
      );

      if (!currentInterval) {
        // Fallback if we can't find the interval
        console.error(
          `Error: No interval found for date=${parsedDate.toDateString()} in milestone map=${milestoneMap.uuid}`,
        );
        return [];
      }

      // Find all results that have deadlines in this interval across all objectives
      // TODO REFINE:
      //  - tree 1 & 2 for this objective
      //  - tree 3 for all objectives
      const allResults = objectives.flatMap((obj) => obj.results);
      const resultsInInterval = filterItemsForInterval(
        allResults,
        currentInterval.start,
        currentInterval.end,
      );

      // Categorize as milestones (key results) or inchstones (regular results)
      const milestones = resultsInInterval.filter((result) => result.isKeyResult);
      const inchstones = resultsInInterval.filter((result) => !result.isKeyResult);

      // Determine the primary type (milestone takes precedence over inchstone)
      const hasMilestones = milestones.length > 0;
      const hasInchstones = inchstones.length > 0;

      let stoneType = '';
      let stoneTypeShort = '';

      if (hasMilestones) {
        stoneType = 'Milestone';
        stoneTypeShort = 'M';
      } else if (hasInchstones) {
        stoneType = 'Inchstone';
        stoneTypeShort = 'I';
      }

      // Determine status color - extract from currentStatus.statusCode
      let statusCode = currentStatus?.statusCode || '';
      let statusColor = '';
      if (statusCode === 'g') {
        statusColor = 'green';
      } else if (statusCode === 'y') {
        statusColor = 'yellow';
      } else if (statusCode === 'r') {
        statusColor = 'red';
      } else if (statusCode === 'p') {
        statusColor = 'gray';
      } else if (statusCode === '?') {
        statusColor = 'gray';
      } else {
        statusColor = '';
      }

      // TODO:
      //  - tree 1 & 2 for this objective
      //  - tree 3 for all objectives

      // Format the date consistently
      const dateOnly = parsedDate.toLocaleDateString();

      // Create the three label variants
      const defaultLabel = stoneType
        ? `${dateOnly} (${stoneType}, ${statusColor})`
        : `${dateOnly} (${statusColor})`;

      const shortLabel = stoneType ? `${dateOnly} (${stoneTypeShort})` : dateOnly;

      return [
        {
          key: 'default',
          selectOption: `Default: [date] ([Inchstone|Milestone], [color])`,
          value: defaultLabel,
        },
        {
          key: 'short',
          selectOption: `Short: [date] (I|M)`,
          value: shortLabel,
        },
        {
          key: 'date',
          selectOption: `Date-only: [date]`,
          value: dateOnly,
        },
      ];

    case 'Status':
      return [
        {
          key: 'default',
          selectOption: 'Default',
          value: suggestedLabel,
        },
      ];

    default:
      console.error(
        'Error: No default label found.',
        'type:',
        type,
        'suggestedLabel:',
        suggestedLabel,
      );
      return [];
  }
}

// Utility function to get general info for screen readers
function getGeneralInfo(type?: string): string {
  switch (type) {
    case 'Milestone Map':
      return 'A milestone map contains all objectives and their progress over time';
    case 'Major Objective':
      return 'A major objective that contains sub-objectives or detailed information';
    case 'Objective':
      return 'An objective represents a specific project or initiative';
    case 'Status Report Date':
      return 'Status reporting date with associated status information';
    case 'Status':
      return 'Status information for an objective at a specific date';
    case 'Key Result':
      return 'A key result represents a measurable outcome';
    case 'Key Results':
      return 'Container for key results and milestones';
    case 'Plan':
      return 'A plan or task related to achieving the objective';
    case 'Plans & Tasks':
      return 'Container for plans and tasks';
    case 'Owner':
      return 'The person responsible for this objective';
    case 'Additional Information':
      return 'Additional details about this objective';
    default:
      return 'Tree item with additional information';
  }
}

// Helper function to create milestone map root item removed - now handled by new architecture

// Helper function to create TreeData structure removed - now handled by converter

// Shared function to build objective hierarchy removed - now handled by new tree builders

// Legacy tree building functions removed - now using new architecture
// The new system uses:
// - buildKeyDetailsDataNode(), buildObjectivesDataNode(), buildDatesDataNode() to create DataNodes
// - convertToTreeData() to convert DataNodes to TreeData
// - Label generators to handle label creation

// Icon configurations now handled by converter in new architecture

export function EmbeddedTreeView({
  milestoneMapId,
  includeRelatedMaps: propIncludeRelatedMaps = false,
}: EmbeddedTreeViewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [includeRelatedMaps, setIncludeRelatedMaps] = useState(propIncludeRelatedMaps);
  const [searchParams] = useSearchParams();
  const { mapShortId } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  // Get milestone map data from Redux store
  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.shortId === mapShortId),
  );
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);
  const dependencyRecords = useSelector((state: RootState) => state.map3Data.dependencyRecords);
  const allMilestoneMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);

  // Get objectives for this map
  const objectives = useMemo(
    () =>
      milestoneMap?.objectiveUuids
        ?.map((uuid) => allObjectives.find((obj) => obj.uuid === uuid))
        .filter((obj): obj is Objective1 => obj !== undefined) || [],
    [milestoneMap?.objectiveUuids, allObjectives],
  );

  // Dependency logic for related maps
  const { allRelatedMilestoneMaps } = useAllRelativesData(mapShortId || '');

  // Simple approach: show all related maps when checkbox is checked
  const visibleMaps = useMemo(() => {
    if (!includeRelatedMaps || !milestoneMap) {
      return [milestoneMap].filter(Boolean) as MilestoneMap1[];
    }
    // When "Include Related Maps" is checked, show ALL related maps
    return allRelatedMilestoneMaps;
  }, [includeRelatedMaps, milestoneMap, allRelatedMilestoneMaps]);

  // Check for local development parameter
  const localPort = searchParams.get('local');
  const isLocalDev = Boolean(localPort);

  // Determine iframe URL based on local parameter
  const iframeUrl = isLocalDev
    ? `http://localhost:${localPort}/v1/embeddable-treeview`
    : 'https://treeview-1ae66.web.app/v1/embeddable-treeview';

  // Determine expected origin for security check
  const expectedOrigin = isLocalDev
    ? `http://localhost:${localPort}`
    : 'https://treeview-1ae66.web.app';

  useEffect(() => {
    // Initialize label generators on component mount
    try {
      registerAllLabelGenerators();
    } catch (error) {
      console.error('❌ Error initializing label generators:', error);
    }

    function handleMessage(event: MessageEvent) {
      // Verify the message is from our iframe (basic security check)
      if (event.origin !== expectedOrigin) {
        return;
      }

      if (event.data && typeof event.data === 'object') {
        const messageData: MessageData = event.data;

        switch (messageData.type) {
          case 'IFRAME_READY':
            setIsIframeReady(true);

            // Send milestone map data once iframe is ready
            setTimeout(() => {
              sendMilestoneMapData();
            }, 500); // Small delay to ensure iframe is fully ready
            break;
          case 'INCLUDE_RELATED_MAPS_CHANGED':
            if (
              messageData.payload &&
              typeof messageData.payload.includeRelatedMaps === 'boolean'
            ) {
              setIncludeRelatedMaps(messageData.payload.includeRelatedMaps);
            } else {
              console.error(
                '❌ Invalid payload for INCLUDE_RELATED_MAPS_CHANGED:',
                messageData.payload,
              );
            }
            break;
          case 'TREE_ITEM_EDIT_REQUEST':
            handleEditRequest(messageData.payload);
            break;
          default:
        }
      }
    }

    function sendMilestoneMapData() {
      if (!iframeRef.current || !iframeRef.current.contentWindow || !milestoneMap) {
        return;
      }

      try {
        const trees: TreeData[] = [];

        if (!includeRelatedMaps || visibleMaps.length === 1) {
          // Single map mode (original behavior)
          // TODO: Uncomment for Key Details tree (hidden for demo)
          // const keyDetailsDataNode = buildKeyDetailsDataNode(milestoneMap, objectives, dependencyRecords, allMilestoneMaps);
          const objectivesDataNode = buildObjectivesDataNode(
            milestoneMap,
            objectives,
            dependencyRecords,
            allMilestoneMaps,
          );
          const datesDataNode = buildDatesDataNode(
            milestoneMap,
            objectives,
            dependencyRecords,
            allMilestoneMaps,
          );

          trees.push(
            // TODO: Uncomment for Key Details tree (hidden for demo)
            // convertToTreeData(
            //   keyDetailsDataNode,
            //   'Key Details',
            //   'Map > Major Objective > Objective > Key Details (Key Details Includes: Milestones, owners, plans & tasks, and additional information; this is for an overview, does not include detailed status reports)',
            //   `${milestoneMap.uuid}-tree-key-details`,
            // ),
            convertToTreeData(
              objectivesDataNode,
              'Objectives',
              'Map > Major Objective > Objective > Reporting Date > Status Report (Recommended for reviewing status objective by objective or project by project, including all status updates, future plans, comments, etc.)',
              `${milestoneMap.uuid}-tree-objectives`,
            ),
            convertToTreeData(
              datesDataNode,
              'Dates',
              'Map > Reporting Date > Objective > Status Report (Recommended for reviewing status of all objectives or projects for one particular time interval, like the status of everythign as of this week)',
              `${milestoneMap.uuid}-tree-dates`,
            ),
          );
        } else {
          // Multi-map mode - Build 3 tree types, each containing all visible maps

          // Create synthetic container nodes that hold multiple milestone maps as children
          // TODO: Uncomment for Key Details tree (hidden for demo)
          // const keyDetailsContainerNode = {
          //   id: 'multi-map-key-details-container',
          //   data: { title: 'Related Maps - Key Details' },
          //   type: 'Container',
          //   children: visibleMaps.map((map) => {
          //     const mapObjectives = allObjectives.filter((obj) =>
          //       map.objectiveUuids.includes(obj.uuid),
          //     );
          //     return buildKeyDetailsDataNode(map, mapObjectives);
          //   }),
          // };

          const objectivesContainerNode = {
            id: 'multi-map-objectives-container',
            data: { title: 'All Maps - Objectives' },
            type: 'Container',
            children: visibleMaps.map((map) => {
              const mapObjectives = allObjectives.filter((obj) =>
                map.objectiveUuids.includes(obj.uuid),
              );
              return buildObjectivesDataNode(
                map,
                mapObjectives,
                dependencyRecords,
                allMilestoneMaps,
              );
            }),
          };

          const datesContainerNode = {
            id: 'multi-map-dates-container',
            data: { title: 'All Maps - Dates' },
            type: 'Container',
            children: visibleMaps.map((map) => {
              const mapObjectives = allObjectives.filter((obj) =>
                map.objectiveUuids.includes(obj.uuid),
              );
              return buildDatesDataNode(map, mapObjectives, dependencyRecords, allMilestoneMaps);
            }),
          };

          // Convert container nodes to TreeData - each will have multiple map roots
          trees.push(
            // TODO: Uncomment for Key Details tree (hidden for demo)
            // convertToTreeData(
            //   keyDetailsContainerNode,
            //   'Key Details',
            //   'All Maps: Map > Major Objective > Objective > Key Details (Overview with milestones, owners, plans & tasks)',
            //   'multi-map-key-details',
            // ),
            convertToTreeData(
              objectivesContainerNode,
              'Objectives',
              'All Maps: Map > Major Objective > Objective > Reporting Date > Status Report (Detailed status by objective)',
              'multi-map-objectives',
            ),
            convertToTreeData(
              datesContainerNode,
              'Dates',
              'All Maps: Map > Reporting Date > Objective > Status Report (Status by time interval)',
              'multi-map-dates',
            ),
          );
        }

        // Determine which root type to exclude by default
        // This promotes root children to top-level and uses root label as tree title
        const defaultExcludedDataTypes =
          !includeRelatedMaps || visibleMaps.length === 1
            ? ['MilestoneMap'] // Single map mode: exclude MilestoneMap root
            : ['Container']; // Multi-map mode: exclude Container root

        const payload: MilestoneMapDataPayload = {
          trees,
          milestoneMapId: milestoneMap.uuid,
          timestamp: new Date().toISOString(),
          defaultExcludedDataTypes,
        };

        iframeRef.current.contentWindow.postMessage(
          {
            type: 'MILESTONE_MAP_DATA',
            payload,
          },
          expectedOrigin,
        );
      } catch (error) {
        console.error('❌ Error creating trees with new architecture:', error);
      }
    }

    async function handleEditRequest(payload: any) {
      if (!payload || !payload.itemId || !payload.edits || !Array.isArray(payload.edits)) {
        console.error('❌ Invalid edit request payload:', payload);
        sendEditResponse(payload?.itemId, false, 'Invalid edit request payload');
        return;
      }

      console.log('📝 Processing edit request for item:', payload.itemId);
      console.log('📝 Edits:', payload.edits);

      try {
        let hasValidEdits = false;

        // Process each edit
        for (const edit of payload.edits) {
          // TODO: Harden check here. Cross check with editableElement items we are sending.
          if (edit.field === 'title' && edit.valueType === 'text') {
            await processEditRequest(payload.itemId, payload.itemType, edit.newValue);
            hasValidEdits = true;
          } else {
            console.warn('⚠️ Unsupported edit type:', edit);
          }
        }

        if (!hasValidEdits) {
          sendEditResponse(payload.itemId, false, 'No supported edits found');
          return;
        }

        // Send updated tree data to refresh the iframe
        setTimeout(() => {
          sendMilestoneMapData();
          sendEditResponse(payload.itemId, true, 'Edit processed successfully');
        }, 100); // Small delay to ensure state updates have propagated
      } catch (error) {
        console.error('❌ Error handling edit request:', error);
        sendEditResponse(
          payload.itemId,
          false,
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    }

    function sendEditResponse(itemId: string, success: boolean, message?: string) {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            type: 'TREE_ITEM_EDIT_RESPONSE',
            payload: {
              success,
              itemId,
              message,
              timestamp: new Date().toISOString(),
            },
          },
          expectedOrigin,
        );
        console.log(`${success ? '✅' : '❌'} Edit response sent to TreeView:`, {
          success,
          itemId,
          message,
        });
      }
    }

    // TODO: newTitle is hardcoded here for POC. We need to handle all editableElement types.
    async function processEditRequest(itemId: string, itemType: string, newTitle: string) {
      try {
        if (itemType === 'MajorObjective' || itemType === 'Objective') {
          // Find the objective to update
          const objective = allObjectives.find((obj) => obj.uuid === itemId);
          if (objective) {
            console.log(
              '📝 Updating objective title:',
              itemId,
              'from',
              objective.text,
              'to',
              newTitle,
            );
            // Use map3PendingSlice for actual Firestore persistence
            await dispatch(
              updateObjective({
                uuid: itemId,
                updates: { text: newTitle },
              }),
            ).unwrap();
          } else {
            console.error('❌ Objective not found:', itemId);
            throw new Error(`Objective not found: ${itemId}`);
          }
        } else if (itemType === 'MilestoneMap') {
          // Find the milestone map to update
          const map = visibleMaps.find((m) => m.uuid === itemId);
          if (map) {
            console.log(
              '📝 Updating milestone map title:',
              itemId,
              'from',
              map.title,
              'to',
              newTitle,
            );
            // Use map3PendingSlice for actual Firestore persistence
            await dispatch(
              updateMilestoneMap({
                uuid: itemId,
                updates: { title: newTitle },
              }),
            ).unwrap();
          } else {
            console.error('❌ Milestone map not found:', itemId);
            throw new Error(`Milestone map not found: ${itemId}`);
          }
        } else {
          console.warn('⚠️ Unsupported item type for edit:', itemType);
          throw new Error(`Unsupported item type: ${itemType}`);
        }
      } catch (error) {
        console.error('❌ Error processing edit request:', error);
        throw error; // Re-throw so handleEditRequest can catch it
      }
    }

    // Listen for messages from iframe
    window.addEventListener('message', handleMessage);

    // Send data immediately if iframe is already ready and we have data
    if (isIframeReady && milestoneMap) {
      sendMilestoneMapData();
    }

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    milestoneMap,
    objectives,
    expectedOrigin,
    isIframeReady,
    includeRelatedMaps,
    visibleMaps,
    allObjectives,
  ]);

  return (
    <div className="h-full w-full">
      <div className="mb-4">
        <DebugInfo
          isLocalDev={isLocalDev}
          localPort={localPort}
          iframeUrl={iframeUrl}
          isIframeReady={isIframeReady}
        />
      </div>

      <iframe
        ref={iframeRef}
        src={iframeUrl}
        style={{
          width: '100%',
          border: 'none',
          minHeight: '600px',
          height: 'calc(100vh - 200px)', // Responsive height
        }}
        title="Milestone Map Tree View"
        loading="lazy"
      />
    </div>
  );
}
